import React, { useEffect } from "react";
import { useI18n } from "@/i18n/I18nContext";
import NewsletterForm from "@/components/NewsletterForm";
import { CheckCircle2, ShieldCheck, GraduationCap, UsersRound, Building2, FileCheck, HandCoins } from "lucide-react";

function setMeta(name, content) {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function Home() {
  const { t, lang } = useI18n();

  useEffect(() => {
    document.title = t("meta.title");
    setMeta("description", t("meta.description"));
    document.documentElement.setAttribute("lang", lang === "mx" ? "es-MX" : "en");
  }, [lang, t]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-[#0F2942] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#2A6B7C] px-3 py-1 text-[11px] font-mono uppercase tracking-widest text-[#94E4C1]">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#94E4C1]" />
              {t("hero.badge")}
            </div>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
              {t("hero.h1_line1")}
              <span className="block text-[#94E4C1]">{t("hero.h1_line2")}</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-200 max-w-2xl leading-relaxed">
              {t("hero.subhead")}
            </p>
            <ul className="mt-8 space-y-3 text-sm text-slate-100">
              {[t("hero.bullet_1"), t("hero.bullet_2"), t("hero.bullet_3")].map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 size={18} className="mt-0.5 text-[#94E4C1]" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex items-center gap-4">
              <div className="h-14 w-14 shrink-0 rounded-full bg-[#94E4C1] text-[#0F2942] grid place-items-center text-lg font-bold">
                {t("metrics.kpi_1_value")}
              </div>
              <div className="text-xs text-slate-300 leading-relaxed max-w-[260px]">
                {t("metrics.kpi_1_label")}
              </div>
            </div>
          </div>
          <div className="lg:col-span-5" id="lead-form-wrap">
            <NewsletterForm variant="hero" />
          </div>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-[#0F2942]/0 pointer-events-none" />
      </section>

      {/* METRICS BAR */}
      <section className="bg-white border-y border-[#E2E8F0]" aria-label="metrics">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <p className="text-xs font-mono uppercase tracking-widest text-[#2A6B7C] text-center">
            {t("metrics.title")}
          </p>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="text-center" data-testid={`metric-${n}`}>
                <div className="text-3xl sm:text-4xl font-bold text-[#0F2942]">
                  {t(`metrics.kpi_${n}_value`)}
                </div>
                <div className="mt-1 text-xs text-[#475569] leading-relaxed">
                  {t(`metrics.kpi_${n}_label`)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS / PROGRAMS */}
      <section id="programs" className="bg-[#F8F9FA] py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs font-mono uppercase tracking-widest text-[#2A6B7C]">
              {t("benefits.eyebrow")}
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0F2942] tracking-tight">
              {t("benefits.title")}
            </h2>
            <p className="mt-4 text-base text-[#475569] leading-relaxed">
              {t("benefits.subtitle")}
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { key: "1", icon: <HandCoins size={22} className="text-[#1B4D3E]" />, img: "family.jpg" },
              { key: "2", icon: <GraduationCap size={22} className="text-[#1B4D3E]" />, img: "documents.jpg" },
              { key: "3", icon: <UsersRound size={22} className="text-[#1B4D3E]" />, img: "advisory.jpg" },
            ].map((c) => (
              <div key={c.key} className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden" data-testid={`benefit-card-${c.key}`}>
                <img
                  src={`/images/${c.img}`}
                  alt=""
                  loading="lazy"
                  className="w-full h-44 object-cover"
                />
                <div className="p-6">
                  <div className="inline-flex items-center gap-2 rounded-md bg-[#F1F5F9] px-2 py-1">
                    {c.icon}
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-[#0F2942]">
                    {t(`benefits.card_${c.key}_title`)}
                  </h3>
                  <p className="mt-2 text-sm text-[#475569] leading-relaxed">
                    {t(`benefits.card_${c.key}_body`)}
                  </p>
                  <ul className="mt-4 space-y-2 text-sm text-[#0F172A]">
                    {[1, 2, 3].map((p) => (
                      <li key={p} className="flex items-start gap-2">
                        <CheckCircle2 size={16} className="mt-0.5 text-[#1B4D3E]" />
                        <span>{t(`benefits.card_${c.key}_point_${p}`)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LITERACY */}
      <section id="literacy" className="bg-white py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <img
              src="/images/budget-laptop.jpg"
              alt=""
              loading="lazy"
              className="w-full h-[420px] object-cover rounded-2xl border border-[#E2E8F0]"
            />
          </div>
          <div className="lg:col-span-6">
            <p className="text-xs font-mono uppercase tracking-widest text-[#2A6B7C]">
              {t("literacy.eyebrow")}
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-[#0F2942] tracking-tight">
              {t("literacy.title")}
            </h2>
            <p className="mt-4 text-base text-[#475569] leading-relaxed">
              {t("literacy.body")}
            </p>
            <ol className="mt-8 space-y-4">
              {[1, 2, 3, 4].map((n) => (
                <li key={n} className="flex items-start gap-4">
                  <div className="mt-0.5 h-8 w-8 shrink-0 rounded-md bg-[#0F2942] text-white grid place-items-center text-sm font-semibold">
                    {n}
                  </div>
                  <div>
                    <h4 className="text-base font-semibold text-[#0F172A]">
                      {t(`literacy.step_${n}_title`)}
                    </h4>
                    <p className="mt-1 text-sm text-[#475569] leading-relaxed">
                      {t(`literacy.step_${n}_body`)}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* CONSULTATIONS */}
      <section id="consultations" className="bg-[#0F2942] text-white py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <p className="text-xs font-mono uppercase tracking-widest text-[#94E4C1]">
              {t("consultations.eyebrow")}
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">
              {t("consultations.title")}
            </h2>
            <p className="mt-4 text-base text-slate-200 leading-relaxed">
              {t("consultations.body")}
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              {[t("consultations.point_1"), t("consultations.point_2"), t("consultations.point_3")].map((p, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 size={18} className="mt-0.5 text-[#94E4C1]" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <a
              href="#lead-form"
              data-testid="consultations-cta"
              className="mt-8 inline-flex items-center rounded-md bg-white text-[#0F2942] px-5 py-3 text-sm font-semibold hover:bg-[#F1F5F9]"
            >
              {t("consultations.cta")}
            </a>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <img
              src="/images/advisor-meeting.jpg"
              alt=""
              loading="lazy"
              className="w-full h-[440px] object-cover rounded-2xl border border-[#2A6B7C]"
            />
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section id="trust" className="bg-[#F8F9FA] py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs font-mono uppercase tracking-widest text-[#2A6B7C]">
              {t("trust.eyebrow")}
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0F2942] tracking-tight">
              {t("trust.title")}
            </h2>
            <p className="mt-4 text-base text-[#475569] leading-relaxed">
              {t("trust.body")}
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Building2 size={22} className="text-[#1B4D3E]" /> },
              { icon: <ShieldCheck size={22} className="text-[#1B4D3E]" /> },
              { icon: <FileCheck size={22} className="text-[#1B4D3E]" /> },
              { icon: <UsersRound size={22} className="text-[#1B4D3E]" /> },
            ].map((it, idx) => {
              const n = idx + 1;
              return (
                <div key={n} className="bg-white border border-[#E2E8F0] rounded-2xl p-6" data-testid={`trust-card-${n}`}>
                  <div className="inline-flex items-center gap-2 rounded-md bg-[#F1F5F9] px-2 py-1">
                    {it.icon}
                  </div>
                  <h4 className="mt-4 text-base font-semibold text-[#0F2942]">
                    {t(`trust.point_${n}_title`)}
                  </h4>
                  <p className="mt-2 text-sm text-[#475569] leading-relaxed">
                    {t(`trust.point_${n}_body`)}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Testimonials */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2].map((n) => (
              <figure key={n} className="bg-white border border-[#E2E8F0] rounded-2xl p-6" data-testid={`testimonial-${n}`}>
                <blockquote className="text-base text-[#0F172A] leading-relaxed">
                  “{t(`testimonial.quote_${n}`)}”
                </blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <img
                    src={n === 1 ? "/images/meeting.jpg" : "/images/consultant.jpg"}
                    alt=""
                    loading="lazy"
                    className="h-10 w-10 rounded-full object-cover"
                  />
                  <div className="text-sm text-[#475569]">
                    {t(`testimonial.author_${n}`)}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section id="newsletter" className="bg-white py-20 sm:py-24 border-t border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <p className="text-xs font-mono uppercase tracking-widest text-[#2A6B7C]">
              {t("newsletter.eyebrow")}
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-[#0F2942] tracking-tight">
              {t("newsletter.title")}
            </h2>
            <p className="mt-4 text-base text-[#475569] leading-relaxed">
              {t("newsletter.body")}
            </p>
            <img
              src="/images/newsletter-photo.jpg"
              alt=""
              loading="lazy"
              className="mt-8 w-full h-64 object-cover rounded-2xl border border-[#E2E8F0]"
            />
          </div>
          <div className="lg:col-span-6">
            <NewsletterForm variant="newsletter" />
          </div>
        </div>
      </section>
    </main>
  );
}
